import type { ReactNode } from "react";
import Button from "../../components/commons/Button";
import type { PostDetail } from "../../services/webServices/post/types";

const FILE_URL = process.env.REACT_APP_File_URL || "";

const POST_STATUS_MAP = {
  pending: { label: "در انتظار تایید", color: "bg-amber-100 text-amber-700" },
  published: { label: "منتشر شده", color: "bg-green-100 text-green-700" },
  draft: { label: "پیش‌نویس", color: "bg-gray-100 text-gray-700" },
  archived: { label: "بایگانی شده", color: "bg-gray-100 text-gray-700" },
} as const;

interface PostDetailModalProps {
  open: boolean;
  detailLoading: boolean;
  actionLoading: boolean;
  post: PostDetail | null;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  onPreviewImage: (url: string) => void;
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <div className="mt-1 text-sm font-medium text-gray-800">{value}</div>
    </div>
  );
}

export default function PostDetailModal({
  open,
  detailLoading,
  actionLoading,
  post,
  onClose,
  onApprove,
  onReject,
  onPreviewImage,
}: PostDetailModalProps) {
  if (!open) return null;

  const status = post
    ? POST_STATUS_MAP[post.status as keyof typeof POST_STATUS_MAP]
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        <div className="border-b border-gray-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">جزئیات پست</h2>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {detailLoading || !post ? (
            <div className="flex flex-col gap-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 rounded bg-gray-200" />
                  <div className="h-3 w-24 rounded bg-gray-200" />
                </div>
              </div>
              <div className="h-24 rounded-xl bg-gray-200" />
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div key={index} className="h-16 rounded-xl bg-gray-200" />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <img
                    src={
                      post.user.avatar
                        ? `${FILE_URL}${post.user.avatar}`
                        : "/images/avatar-alt.jpg"
                    }
                    alt={post.user.full_name || "کاربر"}
                    className="h-12 w-12 rounded-full border border-gray-200 object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/images/avatar-alt.jpg";
                    }}
                  />
                  <div>
                    <p className="text-sm font-bold text-gray-800">
                      {post.user.full_name || "بدون نام"}
                    </p>
                    <p className="text-xs text-gray-500">
                      @{post.user.username}
                    </p>
                    {post.user.headline && (
                      <p className="mt-1 text-xs text-gray-600">
                        {post.user.headline}
                      </p>
                    )}
                  </div>
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {new Date(post.created_at).toLocaleDateString("fa-IR")}
                </span>
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <p className="mb-2 text-xs text-gray-500">متن پست</p>
                <p className="whitespace-pre-wrap text-sm leading-7 text-gray-800">
                  {post.description || "-"}
                </p>
              </div>

              {post.skills.length > 0 && (
                <div>
                  <p className="mb-2 text-xs text-gray-500">مهارت‌ها</p>
                  <div className="flex flex-wrap gap-2">
                    {post.skills.map((skill) => (
                      <span
                        key={skill.id}
                        className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                      >
                        {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {post.media.length > 0 && (
                <div>
                  <p className="mb-2 text-xs text-gray-500">رسانه‌ها</p>
                  <div className="flex flex-wrap gap-2">
                    {post.media.map((item, index) => (
                      <div key={item.id} className="shrink-0">
                        {item.media_type === "video" ? (
                          <video
                            src={`${FILE_URL}${item.media_file}`}
                            controls
                            className="h-40 w-40 rounded-lg border border-gray-200 bg-black object-cover"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              onPreviewImage(`${FILE_URL}${item.media_file}`)
                            }
                            className="h-40 w-40 cursor-zoom-in overflow-hidden rounded-lg border border-gray-200 bg-gray-50 transition-opacity hover:opacity-80"
                            aria-label={`نمایش تصویر ${index + 1}`}
                          >
                            <img
                              src={`${FILE_URL}${item.media_file}`}
                              alt={`رسانه ${index + 1}`}
                              className="h-full w-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DetailField
                  label="وضعیت"
                  value={
                    <span
                      className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
                        status?.color || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {status?.label || post.status}
                    </span>
                  }
                />
                <DetailField
                  label="انتشار"
                  value={post.is_published ? "منتشر شده" : "منتشر نشده"}
                />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 px-6 py-4">
          <div className="flex flex-wrap items-end justify-end gap-3">
            <Button
              variant="primary"
              style="outline"
              size="md"
              text="بستن"
              loading={false}
              disabled={actionLoading}
              onClick={onClose}
            />
            <Button
              variant="primary"
              style="solid"
              size="md"
              text="تایید و انتشار"
              loading={actionLoading}
              disabled={detailLoading || actionLoading || !post}
              onClick={onApprove}
            />
            <Button
              variant="primary"
              style="solid"
              size="md"
              text="رد"
              loading={actionLoading}
              disabled={detailLoading || actionLoading || !post}
              onClick={onReject}
              customClassName="bg-red-500 hover:bg-red-600 disabled:bg-red-400"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
